<script lang="ts">
import type { InjectionKey, Ref } from 'vue';
import type { EncryptedEnvelope, PairingApplicationPayload } from '@altkey/shared-types';

export type ConnectionState = 'disconnected' | 'pairing' | 'paired' | 'error';
export type PairingRole = 'idle' | 'host' | 'joiner';
export type MobileUiScreen = 'gate' | 'hosting' | 'scanning' | 'joining' | 'paired';

export interface PairingContext {
  readonly connectionState: Readonly<Ref<ConnectionState>>;
  readonly sessionId: Readonly<Ref<string | null>>;
  readonly pairingCode: Readonly<Ref<string | null>>;
  readonly pairingRole: Readonly<Ref<PairingRole>>;
  readonly uiScreen: Readonly<Ref<MobileUiScreen>>;
  readonly errorMessage: Readonly<Ref<string | null>>;
  readonly peerConnected: Readonly<Ref<boolean>>;
  readonly lastEncryptedEnvelope: Readonly<Ref<EncryptedEnvelope | null>>;
  retryScanning: () => void;
  startScanning: () => void;
  startHosting: () => Promise<void>;
  disconnectPairing: () => void;
  sendEncryptedPayload: (payload: PairingApplicationPayload) => Promise<void>;
  decryptEnvelope: (envelope: EncryptedEnvelope) => Promise<unknown>;
}

export const PAIRING_CONTEXT_KEY: InjectionKey<PairingContext> = Symbol('altkey-pairing-context');
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, readonly, ref } from 'vue';
import { RouterView } from 'vue-router';
import jsQR from 'jsqr';

import { t } from '@altkey/i18n';
import {
  type EncryptedWebSocketMessage,
} from '@altkey/shared-types';
import { createClient, type RealtimeChannel } from '@supabase/supabase-js';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL?.replaceAll('"', '') ||
  'https://klprtfaurpiivkchyvvo.supabase.co';
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY?.replaceAll('"', '') ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtscHJ0ZmF1cnBpaXZrY2h5dnZvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODg1MzM1NzIsImV4cCI6MjEwNDEwOTU3Mn0.bNE2Vd9FC-79M-aKKzQkmyp4NCXhVesCsxTj_8WdLgg';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface SessionRow {
  id: string;
  pairing_code: string;
  desktop_public_key: JsonWebKey | null;
  mobile_public_key: JsonWebKey | null;
  paired: boolean;
}

const connectionState = ref<ConnectionState>('disconnected');
const sessionId = ref<string | null>(null);
const pairingCode = ref<string | null>(null);
const pairingRole = ref<PairingRole>('idle');
const errorMessage = ref<string | null>(null);
const peerConnected = ref(false);
const sharedSecretKey = ref<CryptoKey | null>(null);
const lastEncryptedEnvelope = ref<EncryptedEnvelope | null>(null);

let activeSocket: WebSocket | null = null;
let keyPair: CryptoKeyPair | null = null;
let pairingChannel: RealtimeChannel | null = null;
let pairingPollId: ReturnType<typeof setInterval> | null = null;
let privateKeyJwk: JsonWebKey | null = null;
let ownPublicKeyJwkStored: JsonWebKey | null = null;
let storedPeerPublicKeyJwk: JsonWebKey | null = null;

const STORAGE_KEY = 'altkey.pairing.mobile.v1';

interface StoredPairing {
  sessionId: string;
  pairingCode: string;
  publicKeyJwk: JsonWebKey;
  privateKeyJwk: JsonWebKey;
  peerPublicKeyJwk: JsonWebKey | null;
  role: 'host' | 'joiner';
}

function readStoredPairing(): StoredPairing | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPairing;
    if (
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.pairingCode !== 'string' ||
      typeof parsed.publicKeyJwk !== 'object' ||
      parsed.publicKeyJwk === null ||
      typeof parsed.privateKeyJwk !== 'object' ||
      parsed.privateKeyJwk === null
    ) {
      return null;
    }
    const role = parsed.role === 'host' || parsed.role === 'joiner' ? parsed.role : 'joiner';
    return { ...parsed, role };
  } catch {
    return null;
  }
}

function persistPairing(): void {
  if (!sessionId.value || !pairingCode.value || !ownPublicKeyJwkStored || !privateKeyJwk) return;
  if (pairingRole.value !== 'host' && pairingRole.value !== 'joiner') return;
  const payload: StoredPairing = {
    sessionId: sessionId.value,
    pairingCode: pairingCode.value,
    publicKeyJwk: ownPublicKeyJwkStored,
    privateKeyJwk,
    peerPublicKeyJwk: storedPeerPublicKeyJwk,
    role: pairingRole.value,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

function clearStoredPairing(): void {
  localStorage.removeItem(STORAGE_KEY);
}

function stopRealtime(): void {
  if (pairingPollId !== null) {
    clearInterval(pairingPollId);
    pairingPollId = null;
  }
  if (pairingChannel) {
    void supabase.removeChannel(pairingChannel);
    pairingChannel = null;
  }
}

const videoElement = ref<HTMLVideoElement | null>(null);
let scanCanvas: HTMLCanvasElement | null = null;
let scanCanvasCtx: CanvasRenderingContext2D | null = null;
let mediaStream: MediaStream | null = null;
let scanAnimationFrame: number | null = null;
let scanningActive = false;
const scanUiOpen = ref(false);

const uiScreen = computed<MobileUiScreen>(() => {
  if (connectionState.value === 'paired') return 'paired';
  if (scanUiOpen.value) return 'scanning';
  if (pairingRole.value === 'host') return 'hosting';
  if (pairingRole.value === 'joiner' && connectionState.value === 'pairing') return 'joining';
  return 'gate';
});

interface QrPayload {
  readonly sessionId: string;
  readonly pairingCode: string;
  readonly publicKeyJwk?: JsonWebKey;
}

function parsePairingFromScan(raw: string): QrPayload | null {
  const trimmed = raw.trim();
  try {
    const url = new URL(trimmed);
    const sessionId = url.searchParams.get('sessionId');
    const pairingCode = url.searchParams.get('pairingCode');
    if (sessionId && pairingCode) {
      return { sessionId, pairingCode };
    }
  } catch {
    // not an absolute URL
  }

  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (typeof parsed.sessionId === 'string' && typeof parsed.pairingCode === 'string') {
      const payload: QrPayload = {
        sessionId: parsed.sessionId,
        pairingCode: parsed.pairingCode
      };
      if (typeof parsed.publicKeyJwk === 'object' && parsed.publicKeyJwk !== null) {
        return { ...payload, publicKeyJwk: parsed.publicKeyJwk as JsonWebKey };
      }
      return payload;
    }
  } catch {
    return null;
  }
  return null;
}

async function startCamera(): Promise<void> {
  errorMessage.value = null;
  scanUiOpen.value = true;

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
      audio: false,
    });

    if (!videoElement.value) return;
    videoElement.value.srcObject = mediaStream;
    await videoElement.value.play();

    scanCanvas = document.createElement('canvas');
    scanCanvasCtx = scanCanvas.getContext('2d', { willReadFrequently: true });

    scanningActive = true;
    scanAnimationFrame = requestAnimationFrame(scanFrame);
  } catch (error) {
    connectionState.value = 'error';
    errorMessage.value =
      error instanceof Error ? error.message : 'Impossibile accedere alla fotocamera';
  }
}

function stopCamera(): void {
  scanningActive = false;

  if (scanAnimationFrame !== null) {
    cancelAnimationFrame(scanAnimationFrame);
    scanAnimationFrame = null;
  }

  mediaStream?.getTracks().forEach((track) => track.stop());
  mediaStream = null;

  if (videoElement.value) {
    videoElement.value.srcObject = null;
  }
}

function scanFrame(): void {
  if (!scanningActive) return;

  const video = videoElement.value;
  const canvas = scanCanvas;
  const ctx = scanCanvasCtx;

  if (video && canvas && ctx && video.readyState === video.HAVE_ENOUGH_DATA) {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const decoded = jsQR(frame.data, frame.width, frame.height, {
      inversionAttempts: 'dontInvert',
    });

    if (decoded) {
      const payload = parsePairingFromScan(decoded.data);
      if (payload) {
        scanningActive = false;
        stopCamera();
        void connectFromQrPayload(payload);
        return;
      }
    }
  }

  scanAnimationFrame = requestAnimationFrame(scanFrame);
}

function bufferToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToBuffer(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function deriveSharedSecret(peerPublicKeyJwk: JsonWebKey): Promise<void> {
  if (!keyPair) return;

  const peerKey = await crypto.subtle.importKey(
    'jwk',
    peerPublicKeyJwk,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );

  sharedSecretKey.value = await crypto.subtle.deriveKey(
    { name: 'ECDH', public: peerKey },
    keyPair.privateKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );

  storedPeerPublicKeyJwk = peerPublicKeyJwk;
  connectionState.value = 'paired';
  persistPairing();
}

async function sendEncryptedPayload(payload: PairingApplicationPayload): Promise<void> {
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN || !sharedSecretKey.value) {
    return;
  }

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    sharedSecretKey.value,
    plaintext,
  );

  const message: EncryptedWebSocketMessage = {
    type: 'encrypted',
    envelope: {
      v: 1,
      alg: 'AES-GCM-256',
      iv: bufferToBase64(iv),
      ciphertext: bufferToBase64(new Uint8Array(ciphertextBuffer)),
      timestamp: Date.now(),
    },
  };

  activeSocket.send(JSON.stringify(message));
}

async function decryptEnvelope(envelope: EncryptedEnvelope): Promise<unknown> {
  if (!sharedSecretKey.value) {
    throw new Error('[AltKey] Segreto condiviso non ancora stabilito: impossibile decifrare');
  }

  const plaintextBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToBuffer(envelope.iv) },
    sharedSecretKey.value,
    base64ToBuffer(envelope.ciphertext),
  );

  return JSON.parse(new TextDecoder().decode(plaintextBuffer)) as unknown;
}

async function connectFromQrPayload(payload: QrPayload): Promise<void> {
  pairingRole.value = 'joiner';
  connectionState.value = 'pairing';
  errorMessage.value = null;
  sessionId.value = payload.sessionId;
  pairingCode.value = payload.pairingCode;

  try {
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Variabili VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY mancanti');
    }

    keyPair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
      'deriveKey',
    ]);
    const ownPublicKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
    ownPublicKeyJwkStored = ownPublicKeyJwk;
    privateKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);

    const { data, error } = await supabase
      .from('sessions')
      .update({
        mobile_public_key: ownPublicKeyJwk,
        paired: true,
      })
      .eq('id', payload.sessionId)
      .eq('pairing_code', payload.pairingCode)
      .select('desktop_public_key')
      .maybeSingle();

    if (error) {
      throw new Error(error.message);
    }
    if (!data) {
      throw new Error('Sessione non trovata o codice di accoppiamento errato');
    }

    const peerPublicKeyJwk =
      (data.desktop_public_key as JsonWebKey | null) ?? payload.publicKeyJwk ?? null;
    if (!peerPublicKeyJwk) {
      throw new Error('Chiave pubblica desktop assente sulla sessione');
    }
    await deriveSharedSecret(peerPublicKeyJwk);

    peerConnected.value = true;
    scanUiOpen.value = false;
    subscribeToSession(payload.sessionId);
  } catch (error) {
    connectionState.value = 'error';
    errorMessage.value =
      error instanceof Error ? error.message : 'Errore sconosciuto durante il pairing';
  }
}

async function applyPairedRow(row: SessionRow | null | undefined): Promise<void> {
  if (pairingRole.value !== 'host') return;
  if (!row || row.id !== sessionId.value) return;
  if (!row.paired || !row.mobile_public_key) return;
  if (connectionState.value === 'paired') return;
  await deriveSharedSecret(row.mobile_public_key);
  peerConnected.value = true;
  if (pairingPollId !== null) {
    clearInterval(pairingPollId);
    pairingPollId = null;
  }
}

function subscribeToSession(id: string): void {
  stopRealtime();

  pairingChannel = supabase
    .channel('altkey-sessions', {
      config: {
        broadcast: { self: true },
        presence: { key: id },
      },
    })
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'sessions',
      },
      (payload) => {
        const row = payload.new as SessionRow | Record<string, never> | undefined;
        if (!row || typeof row !== 'object' || !('id' in row) || row.id !== id) return;
        if (pairingRole.value === 'joiner') {
          peerConnected.value = true;
          return;
        }
        void applyPairedRow(row as SessionRow).catch((err: unknown) => {
          connectionState.value = 'error';
          errorMessage.value =
            err instanceof Error ? err.message : 'Errore durante la derivazione del segreto';
        });
      },
    )
    .subscribe();

  pairingPollId = setInterval(() => {
    void (async () => {
      const { data } = await supabase.from('sessions').select('*').eq('id', id).maybeSingle();
      if (pairingRole.value === 'host') {
        await applyPairedRow(data as SessionRow | null);
      } else if (data && (data as SessionRow).paired) {
        peerConnected.value = true;
      }
    })();
  }, 1500);
}

async function startHosting(): Promise<void> {
  if (connectionState.value === 'pairing' || connectionState.value === 'paired') return;

  stopCamera();
  scanUiOpen.value = false;
  pairingRole.value = 'host';
  connectionState.value = 'pairing';
  errorMessage.value = null;

  try {
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Variabili VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY mancanti');
    }

    const subtle = globalThis.crypto?.subtle;
    if (!subtle) {
      throw new Error('Web Crypto non disponibile in questo contesto');
    }

    keyPair = await subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      ['deriveKey'],
    );

    ownPublicKeyJwkStored = await subtle.exportKey('jwk', keyPair.publicKey);
    privateKeyJwk = await subtle.exportKey('jwk', keyPair.privateKey);

    const newSessionId = crypto.randomUUID();
    const newPairingCode = Math.floor(100000 + Math.random() * 900000).toString();

    const { error: insertError } = await supabase.from('sessions').insert({
      id: newSessionId,
      pairing_code: newPairingCode,
      desktop_public_key: ownPublicKeyJwkStored,
      paired: false,
    });

    if (insertError) {
      throw new Error(insertError.message);
    }

    sessionId.value = newSessionId;
    pairingCode.value = newPairingCode;
    persistPairing();
    subscribeToSession(newSessionId);
  } catch (err) {
    connectionState.value = 'error';
    errorMessage.value = err instanceof Error ? err.message : 'Impossibile creare una sessione';
  }
}

async function restorePairing(): Promise<boolean> {
  const stored = readStoredPairing();
  if (!stored) return false;

  try {
    const publicKey = await crypto.subtle.importKey(
      'jwk',
      stored.publicKeyJwk,
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      [],
    );
    const privateKey = await crypto.subtle.importKey(
      'jwk',
      stored.privateKeyJwk,
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      ['deriveKey'],
    );
    keyPair = { publicKey, privateKey };
    ownPublicKeyJwkStored = stored.publicKeyJwk;
    privateKeyJwk = stored.privateKeyJwk;
    sessionId.value = stored.sessionId;
    pairingCode.value = stored.pairingCode;
    pairingRole.value = stored.role;
    storedPeerPublicKeyJwk = stored.peerPublicKeyJwk;

    if (stored.peerPublicKeyJwk) {
      await deriveSharedSecret(stored.peerPublicKeyJwk);
      peerConnected.value = true;
    } else if (stored.role === 'host') {
      connectionState.value = 'pairing';
      peerConnected.value = false;
    } else {
      connectionState.value = 'paired';
      peerConnected.value = true;
    }

    subscribeToSession(stored.sessionId);
    return true;
  } catch {
    clearStoredPairing();
    return false;
  }
}

function retryScanning(): void {
  pairingRole.value = 'joiner';
  connectionState.value = 'disconnected';
  errorMessage.value = null;
  void startCamera();
}

function startScanning(): void {
  pairingRole.value = 'joiner';
  connectionState.value = 'disconnected';
  errorMessage.value = null;
  void startCamera();
}

function exitScanMode(): void {
  stopCamera();
  scanUiOpen.value = false;
  if (connectionState.value !== 'paired') {
    connectionState.value = 'disconnected';
    errorMessage.value = null;
    pairingRole.value = 'idle';
  }
}

function disconnectPairing(): void {
  stopRealtime();
  clearStoredPairing();
  activeSocket?.close(1000, 'User disconnected');
  activeSocket = null;
  keyPair = null;
  privateKeyJwk = null;
  ownPublicKeyJwkStored = null;
  storedPeerPublicKeyJwk = null;
  sharedSecretKey.value = null;
  sessionId.value = null;
  pairingCode.value = null;
  pairingRole.value = 'idle';
  peerConnected.value = false;
  lastEncryptedEnvelope.value = null;
  connectionState.value = 'disconnected';
  scanUiOpen.value = false;
}

onMounted(() => {
  void (async () => {
    const restored = await restorePairing();
    if (restored) return;

    const params = new URLSearchParams(window.location.search);
    const sessionIdFromUrl = params.get('sessionId');
    const pairingCodeFromUrl = params.get('pairingCode');
    if (sessionIdFromUrl && pairingCodeFromUrl) {
      void connectFromQrPayload({
        sessionId: sessionIdFromUrl,
        pairingCode: pairingCodeFromUrl,
      });
    }
  })();
});

onBeforeUnmount(() => {
  stopCamera();
  stopRealtime();
  activeSocket?.close(1000, 'App unmounted');
});

provide<PairingContext>(PAIRING_CONTEXT_KEY, {
  connectionState: readonly(connectionState),
  sessionId: readonly(sessionId),
  pairingCode: readonly(pairingCode),
  pairingRole: readonly(pairingRole),
  uiScreen,
  errorMessage: readonly(errorMessage),
  peerConnected: readonly(peerConnected),
  lastEncryptedEnvelope: readonly(lastEncryptedEnvelope),
  retryScanning,
  startScanning,
  startHosting,
  disconnectPairing,
  sendEncryptedPayload,
  decryptEnvelope,
});
</script>

<template>
  <div
    class="fixed inset-0 flex w-full flex-col overflow-hidden bg-altkey-dark-base pb-safe-bottom pl-safe-left pr-safe-right pt-safe-top text-white/90"
  >
    <div v-if="uiScreen !== 'scanning'" class="flex min-h-0 w-full flex-1 flex-col">
      <RouterView v-slot="{ Component }">
        <component :is="Component" class="flex h-full min-h-0 w-full flex-1 flex-col" />
      </RouterView>
    </div>

    <div
      v-if="uiScreen === 'scanning'"
      class="fixed inset-0 z-50 bg-black"
    >
      <video
        ref="videoElement"
        class="absolute inset-0 h-full w-full object-cover"
        playsinline
        muted
      />

      <button
        type="button"
        class="absolute left-4 top-[max(1rem,env(safe-area-inset-top))] z-10 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-black shadow-lg"
        @click="exitScanMode"
      >
        <span aria-hidden="true">←</span>
        {{ t('pairing.goBack') }}
      </button>
    </div>
  </div>
</template>
